/**
 * Monitoring types for campaign health checks and alerting system
 */
import z from "zod";

/**
 * Issue severity levels
 */
export enum IssueSeverity {
  WARNING = "warning",
  ERROR = "error",
  CRITICAL = "critical",
}
export const ISSUE_SEVERITY = {
  WARNING: "warning",
  ERROR: "error",
  CRITICAL: "critical",
} as const;
export type ISSUE_SEVERITY = (typeof ISSUE_SEVERITY)[keyof typeof ISSUE_SEVERITY];

/**
 * Issue types for campaign health checks
 */
export enum IssueType {
  MISSING_COOLDOWN_END = "missing_cooldown_end",
  INVALID_PAUSED_EPOCH_COOLDOWN = "invalid_paused_epoch_cooldown",
  S3_DATABASE_MISMATCH = "s3_database_mismatch",
  S3_FETCH_ERROR = "s3_fetch_error",
}
export const ISSUE_TYPE = {
  MISSING_COOLDOWN_END: "missing_cooldown_end",
  INVALID_PAUSED_EPOCH_COOLDOWN: "invalid_paused_epoch_cooldown",
  S3_DATABASE_MISMATCH: "s3_database_mismatch",
  S3_FETCH_ERROR: "s3_fetch_error",
} as const;
export type ISSUE_TYPE = (typeof ISSUE_TYPE)[keyof typeof ISSUE_TYPE];

/**
 * Individual health check issue
 */
export const CampaignHealthIssueSchema = z.object({
  type: z.enum(ISSUE_TYPE),
  severity: z.enum(ISSUE_SEVERITY),
  message: z.string(),
  details: z.object({
    epochId: z.number().optional(),
    epochEnd: z.string().optional(),
    rewardVaultAddress: z.string().optional(),
    campaignSlug: z.string().optional(),
    databaseCount: z.number().optional(),
    s3Count: z.number().optional(),
    s3EvmCount: z.number().optional(),
    s3TonCount: z.number().optional(),
    missingAddresses: z.array(z.string()).optional(),
    extraAddresses: z.array(z.string()).optional(),
    amountMismatches: z
      .array(
        z.object({
          address: z.string(),
          s3Amount: z.string(),
          dbAmount: z.string(),
          dbAmountWei: z.string(),
        }),
      )
      .optional(),
    isPaused: z.boolean().optional(),
    isFullyPaused: z.boolean().optional(),
    cooldownEnd: z.string().optional(),
    pauseType: z.string().optional(),
    error: z.string().optional(),
  }),
});
export type CampaignHealthIssue = z.infer<typeof CampaignHealthIssueSchema>;

/**
 * Campaign health check result
 */
export const CampaignHealthResultSchema = z.object({
  campaignSlug: z.string(),
  rewardVaultAddress: z.string(),
  isHealthy: z.boolean(),
  lastCheckedAt: z.string(),
  issues: z.array(CampaignHealthIssueSchema),
});
export type CampaignHealthResult = z.infer<typeof CampaignHealthResultSchema>;

/**
 * Campaign summary for monitoring list
 */
export const MonitoredCampaignSummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
  rewardVaultAddress: z.string(),
  status: z.enum(["live", "ended"]),
  endedAt: z.string().optional(),
  latestEpochId: z.number(),
});
export type MonitoredCampaignSummary = z.infer<typeof MonitoredCampaignSummarySchema>;

/**
 * Response for listing monitored campaigns
 */
export const ListMonitoredCampaignsResponseSchema = z.object({
  campaigns: z.array(MonitoredCampaignSummarySchema),
  totalCount: z.number(),
});
export type ListMonitoredCampaignsResponse = z.infer<typeof ListMonitoredCampaignsResponseSchema>;

/**
 * Response for campaign health check
 */
export const GetCampaignHealthResponseSchema = z.object({
  health: CampaignHealthResultSchema,
});
export type GetCampaignHealthResponse = z.infer<typeof GetCampaignHealthResponseSchema>;
